import { useState, useEffect } from "react";
import LandingPage       from "./LandingPage";
import ResponsiveApp     from "./ResponsiveApp";
import AdminPanel        from "./AdminPanel";
import OnboardingWizard  from "./OnboardingWizard";

// ── Forgot Password Page ───────────────────────────────────
function ForgotPasswordPage({ onNav }) {
  const [email,   setEmail]   = useState("");
  const [sent,    setSent]    = useState(false);
  const [error,   setError]   = useState("");
  const [busy,    setBusy]    = useState(false);
  const API = process.env.REACT_APP_API_URL || "http://localhost:3001";

  const s = {
    wrap:  { minHeight:"100vh", background:"#07090d", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Space Grotesk',sans-serif", padding:20 },
    box:   { background:"#0f1117", border:"1px solid #1c2233", borderRadius:12, padding:40, width:"100%", maxWidth:420 },
    logo:  { fontSize:32, color:"#00e5b4", marginBottom:16, textAlign:"center" },
    title: { fontSize:26, fontWeight:800, color:"#dde6f0", marginBottom:6, textAlign:"center" },
    sub:   { fontSize:14, color:"#5a6a82", marginBottom:28, textAlign:"center" },
    label: { display:"block", fontSize:11, color:"#5a6a82", letterSpacing:1, textTransform:"uppercase", marginBottom:6, fontFamily:"monospace" },
    input: { width:"100%", background:"#141820", border:"1px solid #1c2233", borderRadius:6, color:"#dde6f0", fontFamily:"inherit", fontSize:14, padding:"11px 13px", outline:"none", marginBottom:14, display:"block" },
    btn:   { width:"100%", background:"#00e5b4", color:"#07090d", border:"none", borderRadius:6, padding:"13px", fontSize:15, fontWeight:700, cursor:"pointer", marginBottom:16, fontFamily:"inherit" },
    err:   { background:"#ff336618", border:"1px solid #ff336640", borderRadius:6, padding:"10px 14px", fontSize:13, color:"#ff3366", marginBottom:16, textAlign:"center" },
    ok:    { background:"#00e5b418", border:"1px solid #00e5b440", borderRadius:6, padding:"14px", fontSize:13, color:"#00e5b4", marginBottom:16, textAlign:"center", lineHeight:1.6 },
    link:  { textAlign:"center", fontSize:13, color:"#5a6a82" },
    a:     { color:"#00e5b4", cursor:"pointer", fontWeight:600 },
    back:  { textAlign:"center", fontSize:12, color:"#5a6a82", marginTop:16, cursor:"pointer" },
  };

  const submit = async () => {
    setError(""); setBusy(true);
    try {
      const res  = await fetch(`${API}/api/auth/forgot-password`, { method:"POST", headers:{"Content-Type":"application/json"}, body: JSON.stringify({ email }) });
      const data = await res.json();
      if (res.ok) { setSent(true); }
      else { setError(data.error || "Request failed"); }
    } catch { setError("Could not connect to server."); }
    setBusy(false);
  };

  return (
    <div style={s.wrap}>
      <div style={s.box}>
        <div style={s.logo}>⬡</div>
        <div style={s.title}>Reset Password</div>
        <div style={s.sub}>Enter your email and we'll send a reset link.</div>
        {sent ? (
          <div style={s.ok}>✓ Check your inbox — if that email is registered, a reset link has been sent. Check your spam folder if you don't see it within a minute.</div>
        ) : (
          <>
            <label style={s.label}>Email Address</label>
            <input style={s.input} type="email" placeholder="you@company.com" value={email} onChange={e => setEmail(e.target.value)} onKeyDown={e => e.key==="Enter" && submit()} />
            {error && <div style={s.err}>{error}</div>}
            <button style={{ ...s.btn, opacity: busy ? .6 : 1 }} onClick={submit} disabled={busy}>
              {busy ? "Sending..." : "Send Reset Link →"}
            </button>
          </>
        )}
        <div style={s.link}><span style={s.a} onClick={() => onNav("login")}>← Back to sign in</span></div>
      </div>
    </div>
  );
}

// ── Reset Password Page ────────────────────────────────────
function ResetPasswordPage({ token, onNav }) {
  const [pass,    setPass]    = useState("");
  const [confirm, setConfirm] = useState("");
  const [done,    setDone]    = useState(false);
  const [error,   setError]   = useState("");
  const [busy,    setBusy]    = useState(false);
  const API = process.env.REACT_APP_API_URL || "http://localhost:3001";

  const s = {
    wrap:  { minHeight:"100vh", background:"#07090d", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Space Grotesk',sans-serif", padding:20 },
    box:   { background:"#0f1117", border:"1px solid #1c2233", borderRadius:12, padding:40, width:"100%", maxWidth:420 },
    logo:  { fontSize:32, color:"#00e5b4", marginBottom:16, textAlign:"center" },
    title: { fontSize:26, fontWeight:800, color:"#dde6f0", marginBottom:6, textAlign:"center" },
    sub:   { fontSize:14, color:"#5a6a82", marginBottom:28, textAlign:"center" },
    label: { display:"block", fontSize:11, color:"#5a6a82", letterSpacing:1, textTransform:"uppercase", marginBottom:6, fontFamily:"monospace" },
    input: { width:"100%", background:"#141820", border:"1px solid #1c2233", borderRadius:6, color:"#dde6f0", fontFamily:"inherit", fontSize:14, padding:"11px 13px", outline:"none", marginBottom:14, display:"block" },
    btn:   { width:"100%", background:"#00e5b4", color:"#07090d", border:"none", borderRadius:6, padding:"13px", fontSize:15, fontWeight:700, cursor:"pointer", marginBottom:16, fontFamily:"inherit" },
    err:   { background:"#ff336618", border:"1px solid #ff336640", borderRadius:6, padding:"10px 14px", fontSize:13, color:"#ff3366", marginBottom:16, textAlign:"center" },
    ok:    { background:"#00e5b418", border:"1px solid #00e5b440", borderRadius:6, padding:"14px", fontSize:13, color:"#00e5b4", marginBottom:16, textAlign:"center" },
    a:     { color:"#00e5b4", cursor:"pointer", fontWeight:600 },
  };

  const submit = async () => {
    setError("");
    if (pass.length < 8) { setError("Password must be at least 8 characters"); return; }
    if (pass !== confirm) { setError("Passwords do not match"); return; }
    setBusy(true);
    try {
      const res  = await fetch(`${API}/api/auth/reset-password`, { method:"POST", headers:{"Content-Type":"application/json"}, body: JSON.stringify({ token, password: pass }) });
      const data = await res.json();
      if (res.ok) { setDone(true); }
      else { setError(data.error || "Reset failed"); }
    } catch { setError("Could not connect to server."); }
    setBusy(false);
  };

  if (!token) return <div style={s.wrap}><div style={s.box}><div style={{ ...s.err, marginBottom:0 }}>Invalid reset link. <span style={s.a} onClick={() => onNav("forgot-password")}>Request a new one.</span></div></div></div>;

  return (
    <div style={s.wrap}>
      <div style={s.box}>
        <div style={s.logo}>⬡</div>
        <div style={s.title}>New Password</div>
        <div style={s.sub}>Choose a strong password for your account.</div>
        {done ? (
          <>
            <div style={s.ok}>✓ Password updated successfully.</div>
            <button style={s.btn} onClick={() => onNav("login")}>Sign In →</button>
          </>
        ) : (
          <>
            <label style={s.label}>New Password</label>
            <input style={s.input} type="password" placeholder="Min. 8 characters" value={pass} onChange={e => setPass(e.target.value)} />
            <label style={s.label}>Confirm Password</label>
            <input style={s.input} type="password" placeholder="Repeat password" value={confirm} onChange={e => setConfirm(e.target.value)} onKeyDown={e => e.key==="Enter" && submit()} />
            {error && <div style={s.err}>{error}</div>}
            <button style={{ ...s.btn, opacity: busy ? .6 : 1 }} onClick={submit} disabled={busy}>
              {busy ? "Updating..." : "Update Password →"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ── Verify Email Page ──────────────────────────────────────
function VerifyEmailPage({ token, onNav }) {
  const [status, setStatus] = useState("loading");
  const [msg,    setMsg]    = useState("");
  const API = process.env.REACT_APP_API_URL || "http://localhost:3001";

  useEffect(() => {
    if (!token) { setStatus("error"); setMsg("No verification token found."); return; }
    fetch(`${API}/api/auth/verify-email?token=${encodeURIComponent(token)}`)
      .then(r => r.json())
      .then(d => { if (d.message) { setStatus("ok"); setMsg(d.message); } else { setStatus("error"); setMsg(d.error || "Verification failed"); } })
      .catch(() => { setStatus("error"); setMsg("Could not connect to server."); });
  }, [token, API]);

  const s = {
    wrap: { minHeight:"100vh", background:"#07090d", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Space Grotesk',sans-serif", padding:20 },
    box:  { background:"#0f1117", border:"1px solid #1c2233", borderRadius:12, padding:40, width:"100%", maxWidth:420, textAlign:"center" },
    ok:   { background:"#00e5b418", border:"1px solid #00e5b440", borderRadius:6, padding:"14px", fontSize:13, color:"#00e5b4", marginBottom:16 },
    err:  { background:"#ff336618", border:"1px solid #ff336640", borderRadius:6, padding:"14px", fontSize:13, color:"#ff3366", marginBottom:16 },
    btn:  { background:"#00e5b4", color:"#07090d", border:"none", borderRadius:6, padding:"11px 28px", fontSize:14, fontWeight:700, cursor:"pointer", fontFamily:"inherit" },
  };

  return (
    <div style={s.wrap}>
      <div style={s.box}>
        <div style={{ fontSize:40, color:"#00e5b4", marginBottom:16 }}>⬡</div>
        {status === "loading" && <div style={{ color:"#5a6a82" }}>Verifying your email...</div>}
        {status === "ok"      && <><div style={s.ok}>✓ {msg} Your account is now verified.</div><button style={s.btn} onClick={() => onNav("login")}>Sign In →</button></>}
        {status === "error"   && <><div style={s.err}>{msg}</div><button style={s.btn} onClick={() => onNav("login")}>Back to Sign In</button></>}
      </div>
    </div>
  );
}

export default function App() {
  const [page,  setPage]  = useState("landing");
  const [token, setToken] = useState(null);
  const [user,  setUser]  = useState(null);

  const [urlToken, setUrlToken] = useState(null);

  // Restore session and handle deep links on mount
  useEffect(() => {
    const pathname = window.location.pathname;
    const params   = new URLSearchParams(window.location.search);
    const tok      = params.get("token");

    if (pathname === "/verify-email" && tok) {
      setUrlToken(tok);
      setPage("verify-email");
      return;
    }
    if (pathname === "/reset-password" && tok) {
      setUrlToken(tok);
      setPage("reset-password");
      return;
    }

    const savedTok = localStorage.getItem("sentinel_token");
    const savedUsr = localStorage.getItem("sentinel_user");
    if (savedTok && savedUsr) {
      try {
        setToken(savedTok);
        setUser(JSON.parse(savedUsr));
        setPage("app");
      } catch {}
    }
  }, []);

  const onLogin = (tok, usr) => {
    setToken(tok);
    setUser(usr);
    localStorage.setItem("sentinel_token", tok);
    localStorage.setItem("sentinel_user", JSON.stringify(usr));
    setPage("onboarding");
  };

  const onOnboardingComplete = () => {
    setPage("app");
  };

  const onLogout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem("sentinel_token");
    localStorage.removeItem("sentinel_user");
    setPage("landing");
  };

  // Always show dashboard if on "app" page with a token
  if (page === "app" && token) return <ResponsiveApp user={user} onLogout={onLogout} onNav={setPage} />;

  // Admin panel
  if (page === "admin") return <AdminPanel />;

  // Onboarding wizard
  if (page === "onboarding") return <OnboardingWizard user={user} onComplete={onOnboardingComplete} />;

  // Auth pages
  if (page === "login")           return <AuthPage mode="login"    onNav={setPage} onLogin={onLogin} />;
  if (page === "register")        return <AuthPage mode="register" onNav={setPage} onLogin={onLogin} />;
  if (page === "forgot-password") return <ForgotPasswordPage onNav={setPage} />;
  if (page === "verify-email")    return <VerifyEmailPage token={urlToken} onNav={setPage} />;
  if (page === "reset-password")  return <ResetPasswordPage token={urlToken} onNav={setPage} />;

  // Landing / pricing
  return <LandingPage onNav={setPage} />;
}

// ── Auth Page ──────────────────────────────────────────────
function AuthPage({ mode, onNav, onLogin }) {
  const [email, setEmail] = useState("");
  const [pass,  setPass]  = useState("");
  const [name,  setName]  = useState("");
  const [error, setError] = useState("");
  const [busy,  setBusy]  = useState(false);
  const isLogin = mode === "login";

  const submit = async () => {
    setError("");
    if (!email || !pass) { setError("Please fill in all fields"); return; }
    if (!isLogin && pass.length < 8) { setError("Password must be at least 8 characters"); return; }

    setBusy(true);
    try {
      const body = isLogin
        ? { email, password: pass }
        : { email, password: pass, full_name: name };

      const res  = await fetch(
        `${process.env.REACT_APP_API_URL || "http://localhost:3001"}/api/auth/${isLogin ? "login" : "register"}`,
        { method:"POST", headers:{ "Content-Type":"application/json" }, body: JSON.stringify(body) }
      );
      const data = await res.json();

      if (data.token) {
        onLogin(data.token, data.user);
      } else {
        setError(data.error || "Something went wrong. Please try again.");
      }
    } catch (err) {
      setError("Could not connect to server. Please try again.");
    }
    setBusy(false);
  };

  const s = {
    wrap:  { minHeight:"100vh", background:"#07090d", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Space Grotesk',sans-serif", padding:20 },
    box:   { background:"#0f1117", border:"1px solid #1c2233", borderRadius:12, padding:40, width:"100%", maxWidth:420 },
    logo:  { fontSize:32, color:"#00e5b4", marginBottom:16, textAlign:"center" },
    title: { fontSize:26, fontWeight:800, color:"#dde6f0", marginBottom:6, textAlign:"center" },
    sub:   { fontSize:14, color:"#5a6a82", marginBottom:28, textAlign:"center" },
    label: { display:"block", fontSize:11, color:"#5a6a82", letterSpacing:1, textTransform:"uppercase", marginBottom:6, fontFamily:"monospace" },
    input: { width:"100%", background:"#141820", border:"1px solid #1c2233", borderRadius:6, color:"#dde6f0", fontFamily:"inherit", fontSize:14, padding:"11px 13px", outline:"none", marginBottom:14, display:"block" },
    btn:   { width:"100%", background:"#00e5b4", color:"#07090d", border:"none", borderRadius:6, padding:"13px", fontSize:15, fontWeight:700, cursor:"pointer", marginBottom:16, fontFamily:"inherit" },
    err:   { background:"#ff336618", border:"1px solid #ff336640", borderRadius:6, padding:"10px 14px", fontSize:13, color:"#ff3366", marginBottom:16, textAlign:"center" },
    link:  { textAlign:"center", fontSize:13, color:"#5a6a82" },
    a:     { color:"#00e5b4", cursor:"pointer", fontWeight:600 },
    back:  { textAlign:"center", fontSize:12, color:"#5a6a82", marginTop:16, cursor:"pointer" },
  };

  return (
    <div style={s.wrap}>
      <div style={s.box}>
        <div style={s.logo}>⬡</div>
        <div style={s.title}>{isLogin ? "Welcome Back" : "Create Account"}</div>
        <div style={s.sub}>{isLogin ? "Sign in to your dashboard." : "Start your free 14-day trial."}</div>

        {!isLogin && (
          <>
            <label style={s.label}>Full Name</label>
            <input style={s.input} placeholder="Jane Smith" value={name} onChange={e => setName(e.target.value)} />
          </>
        )}

        <label style={s.label}>Email Address</label>
        <input style={s.input} type="email" placeholder="you@company.com" value={email} onChange={e => setEmail(e.target.value)} />

        <label style={s.label}>Password</label>
        <input style={s.input} type="password" placeholder={isLogin ? "Your password" : "Min. 8 characters"} value={pass}
          onChange={e => setPass(e.target.value)}
          onKeyDown={e => e.key === "Enter" && submit()} />

        {error && <div style={s.err}>{error}</div>}

        <button style={{ ...s.btn, opacity: busy ? .6 : 1 }} onClick={submit} disabled={busy}>
          {busy ? "Please wait..." : isLogin ? "Sign In →" : "Create Account →"}
        </button>

        <div style={s.link}>
          {isLogin ? "Don't have an account? " : "Already have an account? "}
          <span style={s.a} onClick={() => onNav(isLogin ? "register" : "login")}>
            {isLogin ? "Sign up free" : "Sign in"}
          </span>
        </div>
        {isLogin && (
          <div style={{ ...s.link, marginTop: 10 }}>
            <span style={s.a} onClick={() => onNav("forgot-password")}>Forgot your password?</span>
          </div>
        )}

        <div style={s.back} onClick={() => onNav("landing")}>← Back to home</div>

        <div style={{ marginTop:20, fontSize:11, color:"#2a3a52", textAlign:"center", lineHeight:1.7, borderTop:"1px solid #1c2233", paddingTop:16 }}>
          By signing up you confirm you will only scan systems you own or have written authorisation to test.
        </div>
      </div>
    </div>
  );
}
